import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Trophy, Timer, Award } from "lucide-react";
import Podium from "../components/Podium";
import MiniBoard from "../components/MiniBoard";
import { leaderAvatarSrc } from "../lib/avatars";
import { cacheModeBoard } from "../lib/offlineQueue";
import InfoMore from "../components/InfoMore";

function fmt(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/**
 * Weekly Tournament — Monday-to-Sunday on a single hard seeded board.
 * Top 3 each week earn the "Weekly Champion" badge (frontend-only display
 * for now; full prestige system can come later).
 */
export default function WeeklyTournament() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [board, setBoard] = useState([]);
  const [prevChampion, setPrevChampion] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, l, pc] = await Promise.all([
          api.get("/weekly-tournament/current"),
          api.get("/weekly-tournament/leaderboard?limit=50"),
          api.get("/weekly-tournament/previous-champion"),
        ]);
        setCurrent(c.data);
        setBoard(l.data.leaders || []);
        setPrevChampion(pc.data || null);
        if (c.data?.layout) cacheModeBoard("weekly", c.data.layout, c.data.target_time_sec || 300);
      } catch (e) {
        setCurrent({});
      }
    })();
  }, []);

  if (!current) return <div className="p-8 text-center text-[#5F7161]">…</div>;

  const medal = (rank) => (rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null);

  return (
    <div className="max-w-3xl mx-auto py-4 space-y-4">
      {/* Iter98 — "Last week's podium" — top-3 finishers (gold/silver/
          bronze) with avatars + a mini silhouette of the exact hard board
          that was played. Deep emerald → charcoal gradient, thin gold
          border so the podium pops. */}
      {prevChampion?.podium?.length > 0 && (
        <div
          data-testid="weekly-prev-champion-card"
          className="relative rounded-3xl p-5 text-center overflow-hidden"
          style={{
            background: "linear-gradient(160deg, #3F5042 0%, #2C332A 60%, #1F2620 100%)",
            border: "1px solid rgba(182, 138, 60, 0.45)",
            boxShadow:
              "0 18px 40px -18px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)",
          }}
        >
          {/* Faint radial gold glow */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 50% 25%, rgba(232,163,101,0.18), transparent 55%)",
            }}
          />

          <div className="relative flex flex-col items-center gap-4">
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#E8A365]/80 font-medium">
              {t("weekly.prev_champion_label", { iso: prevChampion.iso_week })}
            </p>

            <Podium entries={prevChampion.podium} />

            {prevChampion.board?.footprint?.length > 0 && (
              <div className="flex flex-col items-center gap-1 pt-1">
                <MiniBoard footprint={prevChampion.board.footprint} size={84} />
                <p className="text-[8px] uppercase tracking-[0.24em] text-white/40">
                  {t("weekly.board_played_label")}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-gradient-to-br from-[#5F7161] to-[#3F5042] text-white p-5">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Trophy size={16} strokeWidth={1.5} className="text-[#E8A365]" />
          <p className="text-xs uppercase tracking-[0.25em] text-white/60">
            {t("weekly.label")} · {current.iso_week}
          </p>
        </div>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl sm:text-2xl font-light mb-1.5 text-center">
          {t("weekly.title")}
        </h1>
        <div className="flex justify-center mb-4">
          <InfoMore light text={t("weekly.subtitle")} testid="weekly-more" />
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <div data-testid="weekly-my-time" className="p-3 rounded-2xl bg-white/10 backdrop-blur-sm">
            <Timer size={16} strokeWidth={1.5} className="text-white/70 mb-1.5" />
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-white/60">
              {t("weekly.your_time")}
            </p>
            <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-light tabular-nums">
              {fmt(current.my_time_sec)}
            </p>
          </div>
          <div data-testid="weekly-reward" className="p-3 rounded-2xl bg-white/10 backdrop-blur-sm">
            <Award size={16} strokeWidth={1.5} className="text-[#E8A365] mb-1.5" />
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-white/60">
              {t("weekly.reward")}
            </p>
            <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-light tabular-nums">
              +{current.reward_coins}
            </p>
          </div>
        </div>

        <Button
          data-testid="weekly-play-btn"
          onClick={() => navigate("/play/weekly/now")}
          className="w-full h-12 rounded-2xl bg-[#E8A365] hover:bg-[#D89455] text-[#2C332A] text-base font-medium"
        >
          {current.already_completed ? t("weekly.play_again") : t("weekly.play_now")}
        </Button>
      </div>

      <div className="rounded-3xl bg-white border border-[#E6E2D8] p-5">
        <h2 className="text-sm uppercase tracking-[0.25em] text-[#8AA48F] mb-4">{t("weekly.leaderboard")}</h2>
        {board.length === 0 && (
          <p className="text-sm text-[#2C332A]/60 py-4">{t("weekly.no_finishers_yet")}</p>
        )}
        <ul className="divide-y divide-[#E6E2D8]">
          {board.map((row) => (
            <li
              key={row.user_id}
              data-testid={`weekly-row-${row.rank}`}
              className={`flex items-center justify-between py-3 ${row.is_me ? "bg-[#F2F5EF] -mx-2 px-2 rounded-xl" : ""}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-7 text-center text-sm text-[#5F7161] tabular-nums">
                  {medal(row.rank) || row.rank}
                </span>
                <div
                  className="w-8 h-8 rounded-full bg-cover bg-center ring-1 ring-[#E6E2D8] shrink-0"
                  style={{ backgroundImage: `url(${leaderAvatarSrc(row)})` }}
                />
                <span className="text-sm text-[#2C332A] truncate">{row.name}</span>
                {row.is_me && <span className="text-xs text-[#5F7161]">{t("leaderboard.you")}</span>}
              </div>
              <span className="text-sm tabular-nums text-[#2C332A]/70">{fmt(row.time_sec)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
